import React, { useCallback, type FC } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import Button, { IconButton } from '@atlaskit/button/new';
import LinkIcon from '@atlaskit/icon/core/link';
import WorldIcon from '@atlaskit/icon/core/globe';
import { PopupSelect } from '@atlaskit/select';
import Toggle from '@atlaskit/toggle';
import TextField from '@atlaskit/textfield';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Flex, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import Link from '@atlaskit/link';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';
import SectionMessage from '@atlaskit/section-message';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import Tooltip from '@atlaskit/tooltip';

import { useCopyToClipboard } from '@confluence/copy-content-link/entry-points/useCopyToClipboard.ts';
import { useRemovePublicLinkPermissionsMutation } from '@confluence/public-links/entry-points/useRemovePublicLinkPermissionsMutation.ts';
import { useAddPublicLinkPermissionsMutation } from '@confluence/public-links/entry-points/useAddPublicLinkPermissionsMutation.ts';
import { usePublicLinkDisablePageMutation } from '@confluence/public-links/entry-points/usePublicLinkDisablePageMutation.ts';
import { usePublicLinkEnablePageMutation } from '@confluence/public-links/entry-points/usePublicLinkEnablePageMutation.ts';
import { PublicLinkPageStatus } from '@confluence/public-links/entry-points/PublicLinkPageQuery.ts';
import {
	PUBLIC_LINKS_SECURITY_DOCUMENTATION,
	LEARN_MORE_PUBLIC_LINKS_HREF,
} from '@confluence/public-links/entry-points/constants.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import { SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from '../../../../controller/Controller.tsx';
import type { GeneralAccessLevel } from '../../../commonTypes.tsx';
import { WorldGreenDotGlyph } from '../../../../svg/WorldGreenDotIcon-svg.tsx';

import { PublicLinkDisabledBody } from './PublicLinkDisabledBody.tsx';

const i18n = defineMessages({
	canView: {
		id: 'share-and-restrict-dialog.public-links.can-view',
		defaultMessage: 'Can view',
		description:
			'Label for the view option in the permissions dropdown in the public links section',
	},
	canEdit: {
		id: 'share-and-restrict-dialog.public-links.can-edit',
		defaultMessage: 'Can edit',
		description:
			'Label for the edit option in the permissions dropdown in the public links section',
	},
	permissionsDropdownLabel: {
		id: 'share-and-restrict-dialog.public-links.permissions-dropdown-label',
		defaultMessage: 'Public link permissions',
		description: 'Label for the permissions dropdown in the public links section',
	},
	learnMore: {
		id: 'share-and-restrict-dialog.public-links.learn-more',
		defaultMessage: 'Learn more',
		description: 'Text to learn more about public links',
	},
	publicLinkOn: {
		id: 'share-and-restrict-dialog.public-links.on',
		defaultMessage: 'Public link is on',
		description: 'Text to indicate that the public link is active (on) for this page',
	},
	publicLinkOff: {
		id: 'share-and-restrict-dialog.public-links.off',
		defaultMessage: 'Public link is off',
		description: 'Text to indicate that the public link is not active (off) for this page',
	},
	anyoneWithLink: {
		id: 'share-and-restrict-dialog.public-links.anyone-with-link',
		defaultMessage: 'Anyone with the link',
		description:
			'Text to explain that anyone with the link can view or edit based on the permission dropdown',
	},
	noSignInRequired: {
		id: 'share-and-restrict-dialog.public-links.no-sign-in-required',
		defaultMessage: 'No sign-in required. <link>Learn more</link>',
		description: 'Text to explain that no sign-in is required to view the content',
	},
	copyButtonText: {
		id: 'share-and-restrict-dialog.public-links.copy-button-text',
		defaultMessage: 'Copy public link',
		description: 'Text for the button to copy the public link',
	},
	viewPublicLinkLabel: {
		id: 'share-and-restrict-dialog.public-links.view-label',
		defaultMessage: 'View',
		description: 'Label for the button to open/view a public link',
	},
	viewPublicLinkToolTip: {
		id: 'share-and-restrict-dialog.public-links.view-tooltip',
		defaultMessage: 'Open public link in a new tab',
		description: 'Tooltip for the button to open/view a public link',
	},
	smartSectionsMessage: {
		id: 'share-and-restrict-dialog.public-links.smart-sections-message',
		defaultMessage:
			'<b>Smart sections don’t work on public editable whiteboards.</b> Any smart sections will be permanently disabled if the public link is made editable. <link>Learn more</link>',
		description:
			'Message to inform the user about permanently disabling smart sections when turning on public editable whiteboards',
	},
});

const publicLinkBodyWrapper = xcss({
	paddingBottom: 'space.300',
});

const worldIconBackground = xcss({
	height: '32px',
	width: '32px',
	borderRadius: 'radius.medium',
	background: token('color.background.accent.gray.subtlest'),
});

const textContainerStyles = xcss({
	marginLeft: 'space.150',
	flexGrow: 1,
});

const dropdownContainer = xcss({
	width: '109px',
	marginRight: 'space.100',
	minWidth: '108px',
	maxWidth: '110px',
});

const sectionMessageWrapperStyles = xcss({
	paddingTop: 'space.150',
	paddingBottom: 'space.150',
});

/**
 * For public link permissions, what level of access is granted for the public link?
 */
export type PublicLinkPermissionsMode = 'view' | 'edit';

const SmartSectionsMessage = () => {
	const appearance = 'information';
	return (
		<Box xcss={sectionMessageWrapperStyles}>
			<SectionMessage appearance={appearance}>
				<Text as="p">
					<FormattedMessage
						{...i18n.smartSectionsMessage}
						values={{
							b: (chunks) => <b>{chunks}</b>,
							link: (chunks: any) => (
								<Link target="_blank" href={LEARN_MORE_PUBLIC_LINKS_HREF}>
									{chunks}
								</Link>
							),
						}}
					/>
				</Text>
			</SectionMessage>
		</Box>
	);
};

const PublicLinkPermissionsDropdown: FC<{
	isEditPublicLink: boolean;
	onChangeDropdown: (newPermission: PublicLinkPermissionsMode) => void;
	isDisabled?: boolean;
	onMenuOpen?: () => void;
}> = ({ isEditPublicLink, onChangeDropdown, isDisabled, onMenuOpen }) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const options = [
		{
			label: formatMessage(i18n.canView),
			value: 'view',
		},
		{
			label: formatMessage(i18n.canEdit),
			value: 'edit',
		},
	];

	const value = options[isEditPublicLink ? 1 : 0];

	return (
		<Box xcss={dropdownContainer}>
			<PopupSelect
				label={formatMessage(i18n.permissionsDropdownLabel)}
				testId="public-link-permissions-dropdown"
				target={({ isOpen, ...triggerProps }) => (
					<Button
						{...triggerProps}
						appearance="subtle"
						isDisabled={isDisabled}
						iconAfter={(iconProps) => <ChevronDownIcon {...iconProps} size="small" />}
					>
						{value.label}
					</Button>
				)}
				onMenuOpen={() => {
					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
							action: 'clicked',
							actionSubject: 'dropdown',
							actionSubjectId: 'publicLinkPermissionsDropdown',
							attributes: {
								currentState: value.value,
							},
						},
					}).fire();
					onMenuOpen?.();
				}}
				options={options}
				isSearchable={false}
				onChange={(option) => onChangeDropdown(option === options[0] ? 'view' : 'edit')}
				spacing="compact"
				minMenuWidth={109}
				popperProps={{
					modifiers: [
						{ name: 'offset', options: { offset: [0, 8] } },
						{
							name: 'preventOverflow',
							enabled: false,
						},
					],
				}}
			/>
		</Box>
	);
};

export type PublicLinkDialogBodyProps = {
	/** Indicates whether the public link is on or off for this content */
	publicLinkOn: boolean;
	/** Indicates whether the public link is disabled at the Space or Product level */
	publicLinkDisabled: boolean;
	/** The current status of the Public Link for this content */
	publicLinkStatus: PublicLinkPageStatus | null;
	/** The URL that leads to the Public view of this content */
	publicShareUrl: string | null | undefined;
	/** Indicates whether the current user is able to change the public link toggle for this content */
	canEditPublicLink: boolean;
	/** Indicates whether it can be an editable public link */
	isPublicLinkEditAllowedForContentType: boolean;
	/** Indicates whether the public link is editable i.e. Editable Public Whiteboards */
	isEditPublicLink: boolean;
	pageId: string;
	refetchPublicLinkState: () => void;
	refetchButtonData: () => void;
	flags: FlagsStateContainer;
	// Analytics
	hasInheritedRestrictions: boolean;
	generalAccessLevel: GeneralAccessLevel;
};

export const PublicLinkDialogBody: FC<PublicLinkDialogBodyProps> = ({
	publicLinkOn,
	publicLinkDisabled,
	publicLinkStatus,
	publicShareUrl,
	canEditPublicLink,
	isPublicLinkEditAllowedForContentType,
	isEditPublicLink,
	pageId,
	refetchPublicLinkState,
	refetchButtonData,
	flags,
	// Analytics
	hasInheritedRestrictions,
	generalAccessLevel,
}) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { copyToClipboard } = useCopyToClipboard();

	const handleCompleted = ({
		refetchState,
		refetchData,
		actionSubject,
		attributes,
	}: {
		refetchState: boolean;
		refetchData: boolean;
		actionSubject: string;
		attributes: any;
	}) => {
		refetchState && refetchPublicLinkState();
		refetchData && refetchButtonData();
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
				action: 'updated',
				actionSubject,
				attributes,
			},
		}).fire();
	};
	const handleError = ({
		error,
		actionSubject,
		attributes,
	}: {
		error: Error;
		actionSubject: string;
		attributes: any;
	}) => {
		markErrorAsHandled(error);
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
				action: 'failed',
				actionSubject,
				attributes: {
					...attributes,
					error: error.message,
				},
			},
		}).fire();
	};

	const { enablePublicLinkForPage } = usePublicLinkEnablePageMutation({
		pageId,
		onCompleted: () => {
			handleCompleted({
				refetchState: true,
				refetchData: true,
				actionSubject: 'publicLinkContentSettings',
				attributes: {
					previousState: PublicLinkPageStatus.OFF,
					newState: PublicLinkPageStatus.ON,
					hasInheritedRestrictions,
					generalAccessLevel,
				},
			});
		},
		onError: (error) => {
			handleError({
				error,
				actionSubject: 'publicLinkContentSettings',
				attributes: {
					previousState: PublicLinkPageStatus.OFF,
					hasInheritedRestrictions,
					generalAccessLevel,
				},
			});
		},
		flags,
	});
	const { disablePublicLinkForPage } = usePublicLinkDisablePageMutation({
		pageId,
		onCompleted: () => {
			handleCompleted({
				refetchState: true,
				refetchData: true,
				actionSubject: 'publicLinkContentSettings',
				attributes: {
					previousState: PublicLinkPageStatus.ON,
					newState: PublicLinkPageStatus.OFF,
					hasInheritedRestrictions,
					generalAccessLevel,
				},
			});
		},
		onError: (error) => {
			handleError({
				error,
				actionSubject: 'publicLinkContentSettings',
				attributes: {
					previousState: PublicLinkPageStatus.ON,
					hasInheritedRestrictions,
					generalAccessLevel,
				},
			});
		},
		flags,
	});
	const { addPublicLinkPermissions } = useAddPublicLinkPermissionsMutation({
		objectId: pageId,
		onCompleted: () => {
			handleCompleted({
				refetchState: true,
				refetchData: false,
				actionSubject: 'publicLinkPermissionChange',
				attributes: {
					previousState: 'view',
					newState: 'edit',
				},
			});
		},
		onError: (error) => {
			handleError({
				error,
				actionSubject: 'publicLinkPermissionChange',
				attributes: {
					previousState: 'view',
				},
			});
		},
		flags,
	});
	const { removePublicLinkPermissions } = useRemovePublicLinkPermissionsMutation({
		objectId: pageId,
		onCompleted: () => {
			handleCompleted({
				refetchState: true,
				refetchData: false,
				actionSubject: 'publicLinkPermissionChange',
				attributes: {
					previousState: 'edit',
					newState: 'view',
				},
			});
		},
		onError: (error) => {
			handleError({
				error,
				actionSubject: 'publicLinkPermissionChange',
				attributes: {
					previousState: 'edit',
				},
			});
		},
		flags,
	});

	const handleToggleChange = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
				action: 'clicked',
				actionSubject: 'toggle',
				actionSubjectId: 'pagePublicLinksToggle',
				attributes: {
					currentState: publicLinkOn ? PublicLinkPageStatus.ON : PublicLinkPageStatus.OFF,
					targetState: publicLinkOn ? PublicLinkPageStatus.OFF : PublicLinkPageStatus.ON,
					previousState: publicLinkOn ? 'enabled' : 'disabled',
					newState: publicLinkOn ? 'disabled' : 'enabled',
					hasInheritedRestrictions,
					generalAccessLevel,
				},
			},
		}).fire();
		publicLinkOn ? void disablePublicLinkForPage() : void enablePublicLinkForPage();
	};

	const handlePermissionsChange = (newPermission: PublicLinkPermissionsMode) => {
		const previousState = isEditPublicLink ? 'edit' : 'view';
		const newState = newPermission;
		if (previousState === newState) return;

		isEditPublicLink ? void removePublicLinkPermissions() : void addPublicLinkPermissions();
	};

	const iconText = publicLinkOn
		? formatMessage(i18n.publicLinkOn)
		: formatMessage(i18n.publicLinkOff);

	const renderPublicLinkDisabledBody = () => (
		<PublicLinkDisabledBody publicLinkStatus={publicLinkStatus} />
	);

	const openPublicLink = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'viewPublicLink',
			},
		}).fire();
		if (publicShareUrl && publicShareUrl !== '') {
			window.open(publicShareUrl, '_blank');
		}
	}, [createAnalyticsEvent, publicShareUrl]);

	const copyPublicLink = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'copyPublicLink',
			},
		}).fire();
		return copyToClipboard(publicShareUrl || '');
	}, [copyToClipboard, createAnalyticsEvent, publicShareUrl]);

	const shouldShowDropdown = publicLinkOn && isPublicLinkEditAllowedForContentType;

	const renderPublicLinkBody = () => {
		return (
			<Stack space="space.200">
				{isPublicLinkEditAllowedForContentType && <SmartSectionsMessage />}
				<Inline alignBlock="center">
					<Flex justifyContent="center" alignItems="center" xcss={worldIconBackground}>
						{publicLinkOn ? (
							<WorldGreenDotGlyph />
						) : (
							<WorldIcon label={iconText} spacing="spacious" />
						)}
					</Flex>
					<Box xcss={textContainerStyles}>
						<Text size="medium" weight="bold">
							{formatMessage(i18n.anyoneWithLink)}
						</Text>
						<br />
						<Flex alignItems="center">
							<Text size="small" color="color.text.subtlest">
								<FormattedMessage
									{...i18n.noSignInRequired}
									values={{
										link: (chunks: any) => (
											<Link target="_blank" href={PUBLIC_LINKS_SECURITY_DOCUMENTATION}>
												{chunks}
											</Link>
										),
									}}
								/>
							</Text>
						</Flex>
					</Box>
					{shouldShowDropdown && (
						<PublicLinkPermissionsDropdown
							isEditPublicLink={isEditPublicLink}
							onChangeDropdown={handlePermissionsChange}
							isDisabled={!canEditPublicLink}
						/>
					)}
					<Toggle
						isChecked={publicLinkOn}
						onChange={handleToggleChange}
						testId="public-links-toggle"
						isDisabled={!canEditPublicLink}
					/>
				</Inline>
				{publicLinkOn && (
					<Inline space="space.150">
						<TextField isCompact isReadOnly value={publicShareUrl || ''} />
						<Button onClick={copyPublicLink} iconBefore={LinkIcon}>
							<FormattedMessage {...i18n.copyButtonText} />
						</Button>
						<Tooltip content={formatMessage(i18n.viewPublicLinkToolTip)}>
							<IconButton
								icon={LinkExternalIcon}
								label={formatMessage(i18n.viewPublicLinkLabel)}
								onClick={openPublicLink}
							/>
						</Tooltip>
					</Inline>
				)}
			</Stack>
		);
	};

	const renderBody = () => {
		if (publicLinkDisabled) {
			return renderPublicLinkDisabledBody();
		}
		return renderPublicLinkBody();
	};

	return <Box xcss={publicLinkBodyWrapper}>{renderBody()}</Box>;
};
